import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import ListingFilters from '@/client/components/admin/ListingManagement/ListingFilters';
import { useListingQuery } from '@/client/api/listing-management/listing.query';
import { EntityFullDetails } from '@/client/types/entity-full';
import Pagination from '@/client/components/common/Pagination';
import { ReviewQueuePanel, ReportedQueuePanel } from '@/client/components/admin/ListingManagement/ListingQueues';
import { deriveEntityStatus, FilterStatus, getDateRange, getListingStatusFilter, Period, SortParams } from '@/client/utils/filters';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import StatsGrid from '@/client/components/common/StatsGrid';
import { useListingStatsQuery } from '@/client/api/listing-management/listing-stats.query';
import { useDataScopeStore } from '@/client/stores/data-scope';
import { buildEntityExportUrl } from '@/client/api/listing-management/entity.query';
import { useTransitionEntityMutation, EntityStatus } from '@/client/api/listing-management/entity.mutation';

import type { PendingAction } from './Listing/types';
import { buildListingStats } from './Listing/stats';
import { useListingTab } from './Listing/useListingTab';
import { ListingsHeader } from './Listing/ListingsHeader';
import { ModerationTabs } from './Listing/ModerationTabs';
import { ListingGridView } from './Listing/ListingGridView';
import { ListingTableView } from './Listing/ListingTableView';
import { TransitionConfirmDialog } from './Listing/TransitionConfirmDialog';

const UnifiedList: React.FC = () => {
  useBreadcrumbs([{ label: 'Listing Management', href: '#' }, { label: 'Listings' }]);

  const {
    searchTerm,
    debouncedSearchTerm,
    isSearchPending,
    filters,
    page,
    limit,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
  } = useListFilters({
    initialFilters: {
      type: '',
      period: 'last30days' as Period,
      status: '' as FilterStatus,
      sort: 'newest' as SortParams,
      tagids: '',
      tag_match: 'any',
      date_from: '',
      date_to: '',
    },
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useListingTab();
  const [view, setView] = useState<'grid' | 'list'>('list');
  const [showAll, setShowAll] = useState(false);
  const testScope = useDataScopeStore((s) => s.scope);
  const [pending, setPending] = useState<PendingAction>(null);
  const [sortBy, setSortBy] = useState('date_added');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const seededEidRef = useRef<string | null>(null);
  const transition = useTransitionEntityMutation();

  const act = (entity: EntityFullDetails['Entity'], next: EntityStatus, label: string) =>
    setPending({ eid: entity.eid, name: entity.name_basic ?? `#${entity.eid}`, status: next, label });

  const confirmPending = async () => {
    if (!pending) return;
    try {
      await transition.mutateAsync({ eid: pending.eid, status: pending.status });
      toast.success(`${pending.name} → ${pending.status}`);
      setPending(null);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update the listing.');
    }
  };

  const openDetail = (eid: number) => navigate(`/admin/listings/${eid}`);

  // Deep-link `?eid=` from admin relationship links seeds the search box so the
  // linked entity is findable in this list.
  useEffect(() => {
    const eid = searchParams.get('eid');
    if (eid && seededEidRef.current !== eid) {
      seededEidRef.current = eid;
      handleSearch(eid);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // `filters.type` is the entity-type id (etid); empty means "all types".
  const getEntityTypeId = () => {
    const etid = Number(filters.type);
    return filters.type && Number.isFinite(etid) ? { category: etid } : {};
  };

  const { data, isLoading, isFetching } = useListingQuery(page, {
    per_page: limit,
    name: debouncedSearchTerm,
    test: testScope,
    // `visibility=all` surfaces drafts/suspended/rejected/deleted for moderation.
    ...(showAll ? { visibility: 'all' } : {}),
    ...getListingStatusFilter(filters.status),
    ...getEntityTypeId(),
    ...getDateRange(filters.period, { start: filters.date_from, end: filters.date_to }),
    ...(filters.tagids?.trim() ? { tagids: filters.tagids.trim(), tag_match: filters.tag_match || 'any' } : {}),
  });

  // The entities endpoint only returns date_added-desc, so column sort is
  // applied client-side over the loaded page.
  const toggleSort = (field: string) => {
    setSortOrder(sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc');
    setSortBy(field);
  };

  const sortedEntities = useMemo(() => {
    const rows = [...(data?.entities ?? [])];
    const dir = sortOrder === 'asc' ? 1 : -1;
    const valueOf = (r: any): string | number => {
      switch (sortBy) {
        case 'name':
          return String(r?.Entity?.name_basic ?? r?.Entity?.name ?? '').toLowerCase();
        case 'type':
          return String(r?.EntityType?.name_friendly ?? r?.EntityType?.name ?? '').toLowerCase();
        case 'price':
          return Number(r?.Prices?.[0]?.price ?? 0);
        case 'status':
          return deriveEntityStatus(r?.Entity ?? r);
        case 'date_added':
        default:
          return new Date(r?.Entity?.date_added ?? 0).getTime();
      }
    };
    return rows.sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });
  }, [data, sortBy, sortOrder]);

  // Stats are scoped by category only — the summary endpoint takes `etid` and
  // ignores the search/period/status filters.
  const scopeEtid = getEntityTypeId().category;
  const { data: stats, isLoading: statsLoading, error: statsError } = useListingStatsQuery(scopeEtid, testScope);
  const listingStats = useMemo(() => buildListingStats(stats), [stats]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-8xl">
        <ListingsHeader view={view} onView={setView} />

        <ModerationTabs
          tab={tab}
          onTab={setTab}
          showAll={showAll}
          onShowAll={setShowAll}
          exportUrl={buildEntityExportUrl({ etid: getEntityTypeId().category, test: testScope })}
        />

        {tab === 'review' && <ReviewQueuePanel />}
        {tab === 'reported' && <ReportedQueuePanel />}

        {tab === 'listings' && (
          <>
            <StatsGrid
              stats={listingStats}
              isLoading={statsLoading}
              error={statsError ? (statsError as Error).message : null}
            />

            <ListingFilters
              onSearch={handleSearch}
              searchValue={searchTerm}
              onFilterChange={(key, value) => handleFilterChange(key as any, value)}
              filters={filters}
              loading={isFetching || isSearchPending}
            />

            {view === 'grid' ? (
              <ListingGridView entities={sortedEntities} onOpenDetail={openDetail} />
            ) : (
              <ListingTableView
                entities={sortedEntities}
                loading={isLoading && !data}
                limit={limit}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onToggleSort={toggleSort}
                onOpenDetail={openDetail}
                onAct={act}
              />
            )}

            <div className="mt-6 bg-white px-4 py-3 border border-gray-200 rounded-lg shadow sm:px-6">
              <Pagination
                total={data?.total_entities ?? 0}
                page={page}
                itemsPerPage={limit}
                limit={limit}
                setPage={handlePageChange}
                setItemsPerPage={handleLimitChange}
                showButtons={true}
                showSelect={true}
                selectOptions={[10, 20, 30, 40, 50]}
              />
            </div>
          </>
        )}
      </div>

      <TransitionConfirmDialog
        pending={pending}
        busy={transition.isPending}
        onCancel={() => setPending(null)}
        onConfirm={confirmPending}
      />
    </div>
  );
};

export default UnifiedList;
